import React, { useState } from "react";
import { FormActions, ModalOverlay } from "../../../components/FormBuilder/CustomForm/FormFiller";
import { useTheme } from "../../../middleware/ThemeContext";
import FormTitle from "../../../components/form/FormTitle";
import styled from "styled-components";
import { CreateFormTheme } from "../../../utils/themes";
import StyledText from "../../../components/StyledComponents/StyledText";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import FormikInputField from "../../../components/Formik/FormikInputField";
import StyledToolTip from "../../../components/StyledComponents/StyledTooltip";
import TooltipIcon from "../../../assets/icons/Tooltip";
import FormikSelect from "../../../components/Formik/FormikSelect";
import ToggleSwitch from "../../../components/StyledComponents/StyledToggle";
import FormikCheckBox from "../../../components/Formik/FormikCheckBox";
import AlertNotice from "../dashboard/Components/AlertNotice";
import PriceAndArchitecture from "./PriceAndArchitecture";
import { MembershipTypeFormValues } from "../../../types/types";
import { Button } from "../../../components/form/Button";
import CloseIcon from "../../../assets/icons/CloseIcon";
import ReceiptIcon from "../../../assets/icons/ReceiptIcon";
import { useCreateNewMembershipMutation } from "../../../api/membershipApiSlice";
import { useToast } from "../../../components/toast/ToastContext";

interface AddNewMembershipTypeProps {
  onClose: () => void;
}

const AddNewMembershipType: React.FC<AddNewMembershipTypeProps> = ({
  onClose,
}) => {
  const { theme } = useTheme();
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [activeTab, setActiveTab] = React.useState("termsLifecycle");
  const showToast = useToast();
  const [CreateMembership] = useCreateNewMembershipMutation();

  const initialValues: MembershipTypeFormValues = {
    // Basic Information
    displayName: "",
    code: "",
    membershipAppliesTo: "contact",
    internalDescription: "",
    publicDescription: "",

    // Terms & Lifecycle
    termType: "",
    effectiveDateRule: "",
    termStartMonth: null,
    enrollmentCutoffMonth: null,
    enrollmentCutoffDay: null,
    renewalInterval: "",
    renewInAdvanceWindow: "30",
    advanceRenewalCustomWindow: "",
    lapsedRenewalPolicy: "",
    lapsedRenewalTermCalculation: "",
    changeMembershipType: "",
    // allowNextTermMembership: '',
    renewalMode: "full",
    joiningForm: "",
    renewalForm: "",

    // Checkboxes
    allowAutoRenew: false,
    allowExpiredToCurrent: false,
    retainJoinDate: false,
    requireApproval: false,
    terminatePrevMemDurationRenewal: false,
    preventRenewalOpenOrderExists: false,

    // Price Architecture
    // eligibilityRulesEnabled: false,
    // eligibilityRules: [],

    basePrice: 0,

    specialPriceTier: [],

    prorationEnabled: false,
    prorationMethod: "monthly",

    multiYearPricing: false,
    multiYearPricingOptions: [
      {
        option: 1,
        duration: 0,
        discountedPrice: 0,
      },
    ],

    paymentTiming: "",

    // creditCardProcessing: '',
    // creditCardFeeAdded: '',
    additionalFeeSettingsEnable: false,
    additionalFeeSettings: [],

    // feeName: '',
    // feeType: '',
    // feeAmount: 0,
    // appliesDuring: '',
    // recurring: '',

    // feeProduct: '',
    // minimumCharges: 0,
    // maximumCharges: 0,

    // Active Status
    activeMembershipType: false,

    // Certificate Settings
    membershipCertificateEnabled: false,
    certificateType: undefined,

    // Non-expiring membership
    nonExpiringMembership: false,
  };

  const validationSchema = Yup.object().shape({
    displayName: Yup.string().required("Display name is required"),
    code: Yup.string()
      .required("Code is required")
      .matches(
        /^[a-zA-Z0-9_-]*$/,
        "Code can only contain letters, numbers, hyphens, and underscores",
      ),
    membershipAppliesTo: Yup.string()
      .oneOf(["organization", "contact"], "Invalid membership applies to value")
      .required("Membership applies to is required"),
    termType: Yup.string()
      .oneOf(["anniversary", "fixed", ""], "Invalid term type")
      .required("Term type is required"),
    basePrice: Yup.number().required("Base price is required"),

    termStartMonth: Yup.number().when("termType", {
    is: "fixed",
    then: (schema) =>
        schema.required("Term start month is required"),
    otherwise: (schema) =>
        schema.notRequired(),
    }),

    internalDescription: Yup.string(),
    publicDescription: Yup.string(),

    enrollmentCutoffMonth: Yup.number(),
    enrollmentCutoffDay: Yup.number(),
    
    effectiveDateRule: Yup.string().oneOf(
      [
        "joinDate",
        "firstDayCurrentMonth",
        "firstDayNextMonth",
        "firstDayClosestMonth",
        "",
      ],
      "Invalid effective date rule",
    ),
    renewalInterval: Yup.string(),
    renewInAdvanceWindow: Yup.string(),
    advanceRenewalCustomWindow: Yup.string().when(
        "renewInAdvanceWindow",
        {
            is: (value: string) => value === "custom",
            then: (schema) =>
            schema.required("Custom window is required"),
            otherwise: (schema) =>
            schema.notRequired(),
        }
    ),
    lapsedRenewalPolicy: Yup.string()
      .oneOf(
        [
          "allow_anytime",
          "allow_within_grace_period",
          "require_rejoin",
          "",
        ],
        "Invalid lapsed renewal policy",
      ),
    lapsedRenewalTermCalculation: Yup.string()
    .oneOf(
        [
        "from_previous_expiration",
        "from_renewal_date",
        "",
        ],
        "Invalid lapsed renewal term calculation"
    )
    .when("lapsedRenewalPolicy", {
        is: (value: string) =>
        value === "allow_within_grace_period" ||
        value === "allow_anytime",
        then: (schema) =>
        schema.required(
            "Lapsed renewal term calculation is required"
        ),
        otherwise: (schema) =>
        schema.notRequired(),
    }),
    changeMembershipType: Yup.string(),
    // allowNextTermMembership: Yup.string(),
    renewalMode: Yup.string(),
    joiningForm: Yup.string(),
    renewalForm: Yup.string(),
    allowAutoRenew: Yup.boolean(),
    allowExpiredToCurrent: Yup.boolean(),
    retainJoinDate: Yup.boolean(),
    requireApproval: Yup.boolean(),
    terminatePrevMemDurationRenewal: Yup.boolean(),
    preventRenewalOpenOrderExists: Yup.boolean(),
    // eligibilityRulesEnabled: Yup.boolean(),
    // eligibilityRules: Yup.array().of(
    //   Yup.string().oneOf(
    //     [
    //       "anyone-can-purchase",
    //       "individuals-only",
    //       "organizations-only",
    //       "only-users-with-specific-attributes",
    //       "only-users-in-certain-chapters",
    //     ],
    //     "Invalid eligibility rule",
    //   ),
    // ),
    prorationEnabled: Yup.boolean(),
    prorationMethod: Yup.string().when("prorationEnabled", {
    is: true,
    then: (schema) =>
        schema.required("Proration method is required"),
    otherwise: (schema) =>
        schema.notRequired(),
    }),
    multiYearPricing: Yup.boolean(),
    active: Yup.boolean(),
    membershipCertificateEnabled: Yup.boolean(),
    certificateType: Yup.string().oneOf(
      ["automatically", "manually"],
      "Invalid certificate type",
    ),
    nonExpiringMembership: Yup.boolean(),

    additionalFeeSettingsEnable: Yup.boolean(),
    // additionalFeeSettings: Yup.array().when("additionalFeeSettingsEnable", {
    //     is: true,
    //     then: (schema) => 
    //         schema.required("Please add fee settings or disable fee settings"),
    //     otherwise: (schema) => 
    //         schema.notRequired(),
    // }),
  });

  const buildPayload = (values: MembershipTypeFormValues) => {
    if (values.termType === 'fixed') {
        return (
            {
                "displayName": values.displayName,
                "code": values.code,
                "membershipAppliesTo": values.membershipAppliesTo,
                "termType": values.termType,
                "termStartMonth": values.termStartMonth,
                "basePrice": values.basePrice,

                "internalDescription": values.internalDescription,
                "publicDescription": values.publicDescription,
                "renewalInterval": values.renewalInterval,
                "renewalInAdvanceWindow": values.renewInAdvanceWindow === 'custom' ? Number(values.advanceRenewalCustomWindow) : Number(values.renewInAdvanceWindow),
                "lapsedRenewalPolicy": values.lapsedRenewalPolicy,
                "lapsedRenewalTermCalculation": values.lapsedRenewalTermCalculation,
                "changeMembershipType": values.changeMembershipType,
                "renewalMode": values.renewalMode,
                "terminatePreviousOnRenewal": values.terminatePrevMemDurationRenewal,
                "prorateRenewalWhenOrderExists": values.preventRenewalOpenOrderExists,
                "requiresApproval": values.requireApproval,
                "isNonExpiring": values.nonExpiringMembership,
                "nextTermEnrollmentCutoffMonth": values.enrollmentCutoffMonth,
                "nextTermEnrollmentCutoffDay": values.enrollmentCutoffDay,
                
                "fees": values.additionalFeeSettings,
                
                "prorationSetting": {
                    "enableProration": values.prorationEnabled,
                    "prorationType": values.prorationMethod,
                },
                
                "feeRules": values.specialPriceTier.map((tier) => ({
                ruleName: tier.tierName,
                ruleConditions: [
                    {
                    field: tier.eligibilityType,
                    operator: tier.operator,
                    value: tier.eligibilityRole,
                    },
                ],
                price: tier.tierPrice,
                // isActive: true,
                })),
            }
        );
    } else if (values.termType === 'anniversary') {
        return (
            {
                "displayName": values.displayName,
                "code": values.code,
                "membershipAppliesTo": values.membershipAppliesTo,
                "termType": values.termType,
                "basePrice": values.basePrice,
                "renewalInterval": values.renewalInterval,
                "renewalMode": values.renewalMode,
                "renewalInAdvanceWindow": values.renewInAdvanceWindow === 'custom' ? Number(values.advanceRenewalCustomWindow) : Number(values.renewInAdvanceWindow),
                "lapsedRenewalPolicy": values.lapsedRenewalPolicy,
                "lapsedRenewalTermCalculation": values.lapsedRenewalTermCalculation,
                "changeMembershipType": values.changeMembershipType,
                "requiresApproval": values.requireApproval,
                
                "fees": values.additionalFeeSettings,
                
                "prorationSetting": {
                    "enableProration": values.prorationEnabled,
                    // "prorationType": values.prorationMethod,
                },
                
                "tenurePricing": values.multiYearPricingOptions.map((price) => ({
                    duration: price.duration,
                    price: price.discountedPrice
                })),
            }
        );
    } else {
        console.log('Invalid Term Type');
    }
  }

  const handleSubmit = (values: MembershipTypeFormValues) => {
    console.log("Form submitted with values:", buildPayload(values));
    const MembershipPayload = buildPayload(values);
    try {
      CreateMembership(MembershipPayload).then((response: any) => {
        if (response?.data) {
          // refetch();
          // setLoading(false);
          // setAddNewNote(false);
          // setUpdateNote(false);
          // setValue("");
          // modifiedDateRefetch();
          onClose();
          showToast(
            "Success",
            // ` Notes ${updateNote ? "updated" : "added"} successfully`,
            `Membership Added Successfully`,
            "success"
          );
        } else {
          // setLoading(false);
          showToast(
            "Action Failed",
            // response?.data?.message || `Error ${updateNote ? "updating" : "adding"} notes`,
            response?.data?.message || 'Failer to add membership',
            "error"
          );
        }
      });
    } catch (e) {
      console.log(e);
    }
  };

  const daysInMonth: Record<string, number> = {
    january: 31,
    february: 28,
    march: 31,
    april: 30,
    may: 31,
    june: 30,
    july: 31,
    august: 31,
    september: 30,
    october: 31,
    november: 30,
    december: 31,
  };

  const toCode = (value: string): string => {
    return value
        .trim()
        .replace(/\s+/g, "_")      // spaces → underscores
        .replace(/[^a-zA-Z0-9_]/g, "") // remove special chars
        .toLowerCase();            // uppercase
  };

  return (
    <ModalOverlay>
      <ModalContent theme={theme} $expanded={true}>
        <FormTitle title="Add New Membership Type" />
        <Formik
          initialValues={initialValues}
          enableReinitialize
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ handleSubmit, setFieldValue, values, touched, errors }) => {
            const totalDays =
            values.enrollmentCutoffMonth &&
              daysInMonth[values?.enrollmentCutoffMonth] || 31;

            const dayOptions = Array.from(
              { length: totalDays },
              (_, index) => ({
                label: String(index + 1),
                value: index + 1,
              }),
            );

            return (
              <Form onSubmit={handleSubmit}>
                <div className="d-flex">
                  <FormContainer className="col-5" theme={theme}>
                    <div className="mb-3">
                      <StyledText
                        className=" mb-2"
                        textColor={theme.CorporateTheme?.colors?.headerColor}
                      >
                        Display Name
                      </StyledText>
                      <FormikInputField
                        name="displayName"
                        placeholder="Enter display name here"
                        onChange={(e) => {
                          const value = e.target.value;
                          setFieldValue("displayName", value);
                          setFieldValue("code", toCode(value));
                          // dispatch(setQueryName({ name: e.target.value }));
                        }}
                      />
                    </div>
                    <div className="mb-3">
                      <StyledText
                        className=" mb-2 d-flex align-items-center gap-1"
                        textColor={theme.CorporateTheme?.colors?.headerColor}
                      >
                        Code
                        <StyledToolTip
                          title={`Used for imports, integrations, and internal reference. Must be unique`}
                          placement="bottom"
                          childrenClass={"ms-0"}
                        >
                          <TooltipIcon color="#767476" />
                        </StyledToolTip>
                      </StyledText>

                      <FormikInputField
                        name="code"
                        placeholder="Enter code here"
                        onChange={(e) => {
                          setFieldValue("code", e.target.value);
                          // dispatch(setQueryName({ name: e.target.value }));
                        }}
                      />
                    </div>
                    <div className="mb-3">
                      <StyledText
                        className=" mb-2"
                        textColor={theme.CorporateTheme?.colors?.headerColor}
                      >
                        Membership Applies To
                      </StyledText>
                      <FormikSelect
                        // className={`${id && "class-disable not-allowed"}`}
                        options={[
                          { label: "Organization", value: "organization" },
                          { label: "Contact", value: "contact" },
                        ]}
                        name="membershipAppliesTo"
                        placeholder="Select membership applies to"
                        onChange={(
                          val:
                            | string
                            | { label: string; value: number | string },
                        ) => {
                          const value =
                            typeof val === "string" ? val : val.value;
                          setFieldValue("membershipAppliesTo", value);
                          // setSelectedRole(String(value));
                        }}
                        // disabled={id ? true : false}
                      />
                    </div>
                    <div className="mb-3">
                      <StyledText
                        className=" mb-2"
                        textColor={theme.CorporateTheme?.colors?.headerColor}
                      >
                        Internal Description
                      </StyledText>

                      <FormikInputField
                        name="internalDescription"
                        as="textarea"
                        placeholder="Enter short description here"
                        onChange={(e) => {
                          setFieldValue("internalDescription", e.target.value);
                          // dispatch(setQueryName({ name: e.target.value }));
                        }}
                      />
                    </div>
                    <div className="mb-3">
                      <StyledText
                        className=" mb-2"
                        textColor={theme.CorporateTheme?.colors?.headerColor}
                      >
                        Public Description
                      </StyledText>

                      <FormikInputField
                        name="publicDescription"
                        as="textarea"
                        placeholder="Enter long description here"
                        onChange={(e) => {
                          setFieldValue("publicDescription", e.target.value);
                          // dispatch(setQueryName({ name: e.target.value }));
                        }}
                      />
                    </div>
                    {isEdit &&
                    <div className="mb-3">
                      <ToggleSwitch
                        label="Do you want to activate this membership type?"
                        checked={values.activeMembershipType}
                        activeColor={theme.CorporateTheme.colors.primary}
                        inactiveColor={
                          theme.CorporateTheme.colors.textSecondary
                        }
                        knobColor={theme.CorporateTheme.colors.background}
                        onChange={() => setFieldValue("activeMembershipType", !values.activeMembershipType)}
                        noPaddingNeeded
                        // disabled={isView}
                      />
                    </div>
                    }
                    {/* {active && (
                    <div className='mb-3'>
                        <div className='mb-3 d-flex align-items-center gap-2'>
                            <FormikCheckBox 
                                name='allowL2PurchaseForL3'
                                label='Allow l2 staff to purchase memberships for l3'
                                borderColor={theme.CorporateTheme?.colors?.textSecondary}
                                bgColor={theme.CorporateTheme?.colors?.primary}
                                checkColor="white"
                                color={theme.CorporateTheme?.colors?.headerColor}
                                size={15}
                            />
                            <StyledToolTip title={`Lorem ipsum dolor sit amet, consectetur adipiscing elit.`} placement='bottom' childrenClass={'ms-0'}>
                                <TooltipIcon color="#767476" />
                            </StyledToolTip>
                        </div>
                        <div className='mb-3 d-flex align-items-center gap-2'>
                            <FormikCheckBox 
                                name='allowL2PurchaseForL3'
                                label='Allow l2 staff to renew memberships for l3'
                                borderColor={theme.CorporateTheme?.colors?.textSecondary}
                                bgColor={theme.CorporateTheme?.colors?.primary}
                                checkColor="white"
                                color={theme.CorporateTheme?.colors?.headerColor}
                                size={15}
                            />
                            <StyledToolTip title={`Lorem ipsum dolor sit amet, consectetur adipiscing elit.`} placement='bottom' childrenClass={'ms-0'}>
                                <TooltipIcon color="#767476" />
                            </StyledToolTip>
                        </div>
                        <div className='mb-3 d-flex align-items-center gap-2'>
                            <FormikCheckBox 
                                name='allowL2PurchaseForL3'
                                label='Allow l2 staff to add members without payment'
                                borderColor={theme.CorporateTheme?.colors?.textSecondary}
                                bgColor={theme.CorporateTheme?.colors?.primary}
                                checkColor="white"
                                color={theme.CorporateTheme?.colors?.headerColor}
                                size={15}
                            />
                            <StyledToolTip title={`Lorem ipsum dolor sit amet, consectetur adipiscing elit.`} placement='bottom' childrenClass={'ms-0'}>
                                <TooltipIcon color="#767476" />
                            </StyledToolTip>
                        </div>
                    </div>
                    )} */}

                    <div className="mb-3" style={{ opacity: 0.5 }}>
                      <ToggleSwitch
                        label="Enable membership certificates?"
                        checked={values.membershipCertificateEnabled}
                        activeColor={theme.CorporateTheme.colors.primary}
                        inactiveColor={
                          theme.CorporateTheme.colors.textSecondary
                        }
                        knobColor={theme.CorporateTheme.colors.background}
                        onChange={() =>
                          setFieldValue(
                            "membershipCertificateEnabled",
                            !values.membershipCertificateEnabled,
                          )
                        }
                        disabled={true}
                        noPaddingNeeded
                      />
                    </div>
                    {values.membershipCertificateEnabled && (
                      <div className="mb-3">
                        <RadioButton theme={theme} className="mb-3">
                          <div>
                            <input
                              type="radio"
                              name="certificateType"
                              value="automatically"
                              checked={
                                values.certificateType === "automatically"
                              }
                              onChange={() =>
                                setFieldValue(
                                  "certificateType",
                                  "automatically",
                                )
                              }
                            />
                          </div>
                          <StyledText
                            htmlTag="span"
                            textColor={
                              theme.CorporateTheme?.colors?.headerColor
                            }
                            fontSize="14px"
                            fontWeight={500}
                          >
                            <span style={{ fontWeight: 600 }}>
                              Generated Automatically
                            </span>{" "}
                            - Certificates are generated automatically based on
                            template you configure
                          </StyledText>
                        </RadioButton>
                        <RadioButton theme={theme}>
                          <div>
                            <input
                              type="radio"
                              name="certificateType"
                              value="manually"
                              checked={values.certificateType === "manually"}
                              onChange={() =>
                                setFieldValue("certificateType", "manually")
                              }
                            />
                          </div>
                          <StyledText
                            htmlTag="span"
                            textColor={
                              theme.CorporateTheme?.colors?.headerColor
                            }
                            fontSize="14px"
                            fontWeight={500}
                          >
                            <span style={{ fontWeight: 600 }}>
                              Manually Created
                            </span>{" "}
                            - Certificates are created outside of Memerberthings
                            and uploaded directly to record
                          </StyledText>
                        </RadioButton>
                      </div>
                    )}
                  </FormContainer>
                  <FormSection className="col-7" theme={theme}>
                    <div
                      className="d-flex gap-3 m-2 p-2"
                      style={{
                        overflowX: "auto",
                        backgroundColor: theme.CorporateTheme.colors.background,
                      }}
                    >
                      <TabButton
                        active={activeTab === "termsLifecycle"}
                        theme={theme}
                        onClick={() => setActiveTab("termsLifecycle")}
                      >
                        <span>Terms & Lifecycle</span>
                      </TabButton>
                      <TabButton
                        active={activeTab === "priceArchitecture"}
                        theme={theme}
                        onClick={() => setActiveTab("priceArchitecture")}
                      >
                        Price Architecture
                      </TabButton>
                      <TabButton
                        active={activeTab === "glConfig"}
                        theme={theme}
                        // onClick={() => setActiveTab("glConfig")}
                        style={{ opacity: 0.5, cursor: 'auto' }}

                      >
                        GL Configuration
                      </TabButton>
                      <TabButton
                        active={activeTab === "emailConfig"}
                        theme={theme}
                        // onClick={() => setActiveTab("emailConfig")}
                        style={{ opacity: 0.5, cursor: 'auto' }}
                      >
                        Email Confirmation
                      </TabButton>
                    </div>
                    <div className="px-4 py-3">
                      {activeTab === "termsLifecycle" && (
                        <div className="" id="terms-lifecycle">
                          <div className="mb-3">
                            <StyledText
                              className=" mb-2"
                              textColor={
                                theme.CorporateTheme?.colors?.headerColor
                              }
                            >
                              Term Type
                            </StyledText>
                            <FormikSelect
                              // className={`${id && "class-disable not-allowed"}`}
                              options={[
                                { label: "Anniversary", value: "anniversary" },
                                { label: "Fixed", value: "fixed" },
                              ]}
                              name="termType"
                              placeholder="Select term type here"
                              onChange={(
                                val:
                                  | string
                                  | { label: string; value: number | string },
                              ) => {
                                const value =
                                  typeof val === "string" ? val : val.value;
                                setFieldValue("termType", value);
                                // setSelectedRole(String(value));
                              }}
                              // disabled={id ? true : false}
                            />
                          </div>
                          <div className="mb-3 d-flex align-items-center justify-content-between">
                            <div>
                              <StyledText
                                className="mb-1"
                                textColor={
                                  theme?.CorporateTheme?.colors?.mirage
                                }
                              >
                                Non-Expiring Membership
                              </StyledText>
                              <StyledText
                                textColor={
                                  theme?.CorporateTheme?.colors?.textSecondary
                                }
                                fontSize="12px"
                                fontWeight={400}
                              >
                                Used for lifetime, staff, internal, honorary, or
                                free memberships.
                              </StyledText>
                            </div>
                            <ToggleSwitch
                              // label="Enable membership certificates?"
                              checked={values.nonExpiringMembership}
                              activeColor={theme.CorporateTheme.colors.primary}
                              inactiveColor={
                                theme.CorporateTheme.colors.textSecondary
                              }
                              knobColor={theme.CorporateTheme.colors.background}
                              onChange={() =>
                                setFieldValue(
                                  "nonExpiringMembership",
                                  !values.nonExpiringMembership,
                                )
                              }
                              // disabled={isView}
                              noPaddingNeeded
                              showTitle={false}
                            />
                          </div>
                          {values.nonExpiringMembership && (
                            <AlertNotice message="This membership never expires. Renewal cycles, term lengths, and expiration settings are disabled." />
                          )}
                          {values.termType !== "" && (
                            <hr
                              style={{
                                color: theme.CorporateTheme.colors.border,
                                opacity: 1,
                              }}
                            />
                          )}

                          {values.termType === "anniversary" && (
                            <>
                              <StyledText className="mb-4">
                                Anniversary Based Settings
                              </StyledText>

                              <div className="mb-3">
                                <StyledText
                                  className=" mb-2"
                                  textColor={
                                    theme.CorporateTheme?.colors?.headerColor
                                  }
                                >
                                  Membership Effective Date Rule
                                </StyledText>
                                <FormikSelect
                                  // className={`${id && "class-disable not-allowed"}`}
                                  options={[
                                    { label: "Join Date", value: "joinDate" },
                                    {
                                      label: "First Day Of Current Month",
                                      value: "firstDayCurrentMonth",
                                    },
                                    {
                                      label: "First Day Of Next Month",
                                      value: "firstDayNextMonth",
                                    },
                                    {
                                      label: "First Day Of Closest Month",
                                      value: "firstDayClosestMonth",
                                    },
                                  ]}
                                  name="effectiveDateRule"
                                  placeholder="Select membership effective date rule"
                                  onChange={(
                                    val:
                                      | string
                                      | {
                                          label: string;
                                          value: number | string;
                                        },
                                  ) => {
                                    const value =
                                      typeof val === "string" ? val : val.value;
                                    setFieldValue("effectiveDateRule", value);
                                    // setSelectedRole(String(value));
                                  }}
                                  // disabled={id ? true : false}
                                />
                              </div>
                            </>
                          )}

                          {values.termType === "fixed" && (
                            <>
                              <StyledText className="mb-4">
                                Calendar Based Settings
                              </StyledText>

                              <div className="mb-3">
                                <StyledText
                                  className=" mb-2"
                                  textColor={
                                    theme.CorporateTheme?.colors?.headerColor
                                  }
                                >
                                  Term Start Month
                                </StyledText>
                                <FormikSelect
                                  // className={`${id && "class-disable not-allowed"}`}
                                  options={[
                                    { label: "January", value: 1 },
                                    { label: "February", value: 2 },
                                    { label: "March", value: 3 },
                                    { label: "April", value: 4 },
                                    { label: "May", value: 5 },
                                    { label: "June", value: 6 },
                                    { label: "July", value: 7 },
                                    { label: "August", value: 8 },
                                    { label: "September", value: 9 },
                                    { label: "October", value: 10 },
                                    { label: "November", value: 11 },
                                    { label: "December", value: 12 },
                                  ]}
                                  name="termStartMonth"
                                  placeholder="Select term start month"
                                  onChange={(
                                    val:
                                      | string
                                      | {
                                          label: string;
                                          value: number | string;
                                        },
                                  ) => {
                                    const value =
                                      typeof val === "string"
                                        ? Number(val)
                                        : Number(val.value);
                                    setFieldValue("termStartMonth", value);
                                    // setSelectedRole(String(value));
                                  }}
                                  // disabled={id ? true : false}
                                />
                              </div>

                              <div className="mb-3">
                                <StyledText
                                  className=" mb-2"
                                  textColor={
                                    theme.CorporateTheme?.colors?.headerColor
                                  }
                                >
                                  Next-Term Enrollment Cutoff Date
                                </StyledText>
                                <div className="d-flex align-items-center gap-3">
                                  <FormikSelect
                                    className={`col`}
                                    options={[
                                      { label: "January", value: 1 },
                                      { label: "February", value: 2 },
                                      { label: "March", value: 3 },
                                      { label: "April", value: 4 },
                                      { label: "May", value: 5 },
                                      { label: "June", value: 6 },
                                      { label: "July", value: 7 },
                                      { label: "August", value: 8 },
                                      {
                                        label: "September",
                                        value: 9,
                                      },
                                      { label: "October", value: 10 },
                                      { label: "November", value: 11 },
                                      { label: "December", value: 12 },
                                    ]}
                                    name="enrollmentCutoffMonth"
                                    placeholder="Select enrollment cutoff month"
                                    onChange={(
                                      val:
                                        | string
                                        | {
                                            label: string;
                                            value: number | string;
                                          },
                                    ) => {
                                      const value =
                                        typeof val === "string"
                                          ? Number(val)
                                          : Number(val.value);
                                      setFieldValue(
                                        "enrollmentCutoffMonth",
                                        value,
                                      );
                                      // setSelectedRole(String(value));
                                    }}
                                    // disabled={id ? true : false}
                                  />
                                  <FormikSelect
                                    className={`col`}
                                    options={dayOptions}
                                    name="enrollmentCutoffDay"
                                    placeholder="Select enrollment cutoff day"
                                    onChange={(
                                      val:
                                        | string
                                        | {
                                            label: string;
                                            value: number | string;
                                          },
                                    ) => {
                                      const value =
                                        typeof val === "string"
                                          ? Number(val)
                                          : Number(val.value);
                                      setFieldValue(
                                        "enrollmentCutoffDay",
                                        value,
                                      );
                                      // setSelectedRole(String(value));
                                    }}
                                    // disabled={id ? true : false}
                                  />
                                </div>
                              </div>
                            </>
                          )}

                          <hr
                            style={{
                              color: theme.CorporateTheme.colors.border,
                              opacity: 1,
                            }}
                          />

                          {values.termType !== "" && (
                            <>
                              <StyledText className="mb-4">
                                Duration & Renewal
                              </StyledText>

                              <div className="mb-3">
                                <StyledText
                                  className=" mb-2"
                                  textColor={
                                    theme.CorporateTheme?.colors?.headerColor
                                  }
                                >
                                  Renewal Interval
                                </StyledText>
                                <FormikSelect
                                  // className={`${id && "class-disable not-allowed"}`}
                                  options={
                                    values.termType === "fixed"
                                      ? [
                                          {
                                            label: "Annually",
                                            value: "annually",
                                          },
                                          {
                                            label: "Every 2 Years",
                                            value: "every_2_years",
                                          },
                                          {
                                            label: "Every 3 Years",
                                            value: "every_3_years",
                                          },
                                        ]
                                      : values.termType === "anniversary"
                                        ? [
                                            {
                                              label: "Monthly",
                                              value: "monthly",
                                            },
                                            {
                                              label: "Quarterly",
                                              value: "quarterly",
                                            },
                                            {
                                              label: "Annually",
                                              value: "annually",
                                            },
                                            {
                                              label: "Every 2 Years",
                                              value: "every_2_years",
                                            },
                                            {
                                              label: "Every 3 Years",
                                              value: "every_3_years",
                                            },
                                          ]
                                        : []
                                  }
                                  name="renewalInterval"
                                  placeholder="Select renewal interval"
                                  onChange={(
                                    val:
                                      | string
                                      | {
                                          label: string;
                                          value: number | string;
                                        },
                                  ) => {
                                    const value =
                                      typeof val === "string" ? val : val.value;
                                    setFieldValue("renewalInterval", value);
                                    // setSelectedRole(String(value));
                                  }}
                                  // disabled={id ? true : false}
                                />
                              </div>

                              <div className="mb-3">
                                <StyledText
                                  className=" mb-2"
                                  textColor={
                                    theme.CorporateTheme?.colors?.headerColor
                                  }
                                >
                                  Renew-in-advance Window
                                </StyledText>
                                <FormikSelect
                                  // className={`${id && "class-disable not-allowed"}`}
                                  options={[
                                    { label: "30 Days", value: 30 },
                                    { label: "60 Days", value: 60 },
                                    { label: "90 Days", value: 90 },
                                    { label: "Custom", value: "custom" },
                                    // {
                                    //   label: "Allowed Anytime",
                                    //   value: "allowed-anytime",
                                    // },
                                  ]}
                                  name="renewInAdvanceWindow"
                                  placeholder="Select renew-in-advance window"
                                  onChange={(
                                    val:
                                      | string
                                      | {
                                          label: string;
                                          value: number | string;
                                        },
                                  ) => {
                                    const value =
                                      typeof val === "string" ? val : val.value;
                                    setFieldValue(
                                      "renewInAdvanceWindow",
                                      value,
                                    );
                                    // setSelectedRole(String(value));
                                  }}
                                  // disabled={id ? true : false}
                                />
                              </div>
                              {values.renewInAdvanceWindow === "custom" && (
                                <div className="mb-3">
                                  <FormikInputField
                                    className="mb-3"
                                    name="advanceRenewalCustomWindow"
                                    type="number"
                                    min={0}
                                    maxChar={120}
                                    // as='textarea'
                                    placeholder="Enter custom Renew In Advance Here"
                                    onChange={(e) => {
                                      setFieldValue(
                                        "advanceRenewalCustomWindow",
                                        e.target.value,
                                      );
                                      // dispatch(setQueryName({ name: e.target.value }));
                                    }}
                                    suffix="Days"
                                  />
                                  <AlertNotice
                                    message="Members can renew up to 120 days before expiration."
                                    noMargin={true}
                                  />
                                </div>
                              )}

                              <div className="mb-3">
                                <StyledText
                                  className=" mb-2"
                                  textColor={
                                    theme.CorporateTheme?.colors?.headerColor
                                  }
                                >
                                  Lapsed Renewal Policy
                                </StyledText>
                                <FormikSelect
                                  // className={`${id && "class-disable not-allowed"}`}
                                  options={[
                                    {
                                      label: "Allow Renewal Anytime",
                                      value: "allow_anytime",
                                    },
                                    {
                                      label:
                                        "Allow Renewal Within Grace Period Only",
                                      value:
                                        "allow_within_grace_period",
                                    },
                                    {
                                      label: "Require Rejoin After Lapse",
                                      value: "require_rejoin",
                                    },
                                  ]}
                                  name="lapsedRenewalPolicy"
                                  placeholder="Select lapsed renewal policy"
                                  onChange={(
                                    val:
                                      | string
                                      | {
                                          label: string;
                                          value: number | string;
                                        },
                                  ) => {
                                    const value =
                                      typeof val === "string" ? val : val.value;
                                    setFieldValue("lapsedRenewalPolicy", value);
                                    // setSelectedRole(String(value));
                                  }}
                                  // disabled={id ? true : false}
                                />
                              </div>

                              {(values.lapsedRenewalPolicy ===
                                "allow_within_grace_period" ||
                                values.lapsedRenewalPolicy ===
                                  "allow_anytime") && (
                                <div className="mb-3">
                                  <StyledText
                                    className=" mb-2"
                                    textColor={
                                      theme.CorporateTheme?.colors?.headerColor
                                    }
                                  >
                                    Lapsed Renewal Term Calculation
                                  </StyledText>
                                  <FormikSelect
                                    // className={`${id && "class-disable not-allowed"}`}
                                    options={[
                                      {
                                        label: "Renew From Previous Expiration",
                                        value: "from_previous_expiration",
                                      },
                                      {
                                        label: "Renew From Renewal Date",
                                        value: "from_renewal_date",
                                      },
                                    ]}
                                    name="lapsedRenewalTermCalculation"
                                    placeholder="Select lapsed renewal term calculation"
                                    onChange={(
                                      val:
                                        | string
                                        | {
                                            label: string;
                                            value: number | string;
                                          },
                                    ) => {
                                      const value =
                                        typeof val === "string"
                                          ? val
                                          : val.value;
                                      setFieldValue(
                                        "lapsedRenewalTermCalculation",
                                        value,
                                      );
                                      // setSelectedRole(String(value));
                                    }}
                                    // disabled={id ? true : false}
                                  />
                                </div>
                              )}

                              <div className="mb-3">
                                <StyledText
                                  className=" mb-2"
                                  textColor={
                                    theme.CorporateTheme?.colors?.headerColor
                                  }
                                >
                                  Change Membership Type
                                </StyledText>
                                <StyledText
                                  className="mb-2"
                                  fontSize="12px"
                                  fontWeight={400}
                                  textColor={
                                    theme.CorporateTheme?.colors?.textSecondary
                                  }
                                >
                                  Changing membership type only applies to the
                                  next renewal period, not the current
                                  membership
                                </StyledText>
                                <FormikSelect
                                  // className={`${id && "class-disable not-allowed"}`}
                                  options={[
                                    {
                                      label: "Not Allowed",
                                      value: "not_allowed",
                                    },
                                    {
                                      label: "Only During Renewal",
                                      value: "only_during_renewal",
                                    },
                                  ]}
                                  name="changeMembershipType"
                                  placeholder="Select change-membership-type"
                                  onChange={(
                                    val:
                                      | string
                                      | {
                                          label: string;
                                          value: number | string;
                                        },
                                  ) => {
                                    const value =
                                      typeof val === "string" ? val : val.value;
                                    setFieldValue(
                                      "changeMembershipType",
                                      value,
                                    );
                                    // setSelectedRole(String(value));
                                  }}
                                  // disabled={id ? true : false}
                                />
                              </div>

                              {/* <div className='mb-3'>
                                    <StyledText
                                        className=" mb-2"
                                        textColor={theme.CorporateTheme?.colors?.headerColor}
                                    >
                                        Allow Next-Term Membership to be Created Before Current Term Ends
                                    </StyledText>
                                    <FormikSelect
                                        // className={`${id && "class-disable not-allowed"}`}
                                        className='mb-3'
                                        options={[
                                            { label: "No", value: "no" },
                                            { label: "Yes", value: "yes" },
                                        ]}
                                        name="allowNextTermMembership"
                                        placeholder="Select allow-next-term-membership"
                                        onChange={(
                                            val:
                                                | string
                                                | { label: string; value: number | string }
                                        ) => {
                                            const value =
                                                typeof val === "string" ? val : val.value;
                                            setFieldValue("allowNextTermMembership", value);
                                            // setSelectedRole(String(value));
                                        }}
                                        // disabled={id ? true : false}
                                    />
                                    <AlertNotice 
                                        message='system will only create the next-term membership at renewal time, preventing multiple active memberships from appearing in the record'
                                    />
                                </div> */}

                              <div className="mb-3">
                                <StyledText
                                  className=" mb-2"
                                  textColor={
                                    theme.CorporateTheme?.colors?.headerColor
                                  }
                                >
                                  Renewal Mode
                                </StyledText>
                                <FormikSelect
                                  // className={`${id && "class-disable not-allowed"}`}
                                  className="mb-3"
                                  options={[
                                    {
                                      label: "Quick Renewal",
                                      value: "quick",
                                    },
                                    {
                                      label: "Full",
                                      value: "full",
                                    },
                                  ]}
                                  name="renewalMode"
                                  placeholder="Select Renewal Mode"
                                  onChange={(
                                    val:
                                      | string
                                      | {
                                          label: string;
                                          value: number | string;
                                        },
                                  ) => {
                                    const value =
                                      typeof val === "string" ? val : val.value;
                                    setFieldValue("renewalMode", value);
                                    // setSelectedRole(String(value));
                                  }}
                                  // disabled={id ? true : false}
                                />
                              </div>

                              <hr
                                style={{
                                  color: theme.CorporateTheme.colors.border,
                                  opacity: 1,
                                }}
                              />

                              <div className="mb-3 d-flex align-items-center justify-content-between">
                                <div>
                                  <StyledText
                                    className="mb-1"
                                    textColor={
                                      theme?.CorporateTheme?.colors?.mirage
                                    }
                                  >
                                    Terminate Previous Membership During Renewal
                                  </StyledText>
                                  <StyledText
                                    textColor={
                                      theme?.CorporateTheme?.colors
                                        ?.textSecondary
                                    }
                                    fontSize="12px"
                                    fontWeight={400}
                                  >
                                    Ends the current active membership period
                                    immediately when the renewal order is
                                    processed. Use for prorated upgrades.
                                  </StyledText>
                                </div>
                                <ToggleSwitch
                                  // label="Enable membership certificates?"
                                  checked={
                                    values.terminatePrevMemDurationRenewal
                                  }
                                  activeColor={
                                    theme.CorporateTheme.colors.primary
                                  }
                                  inactiveColor={
                                    theme.CorporateTheme.colors.textSecondary
                                  }
                                  knobColor={
                                    theme.CorporateTheme.colors.background
                                  }
                                  onChange={() =>
                                    setFieldValue(
                                      "terminatePrevMemDurationRenewal",
                                      !values.terminatePrevMemDurationRenewal,
                                    )
                                  }
                                  // disabled={isView}
                                  noPaddingNeeded
                                  showTitle={false}
                                />
                              </div>

                              <div className="mb-3 d-flex align-items-center justify-content-between">
                                <div>
                                  <StyledText
                                    className="mb-1"
                                    textColor={
                                      theme?.CorporateTheme?.colors?.mirage
                                    }
                                  >
                                    Prevent Renewal When Open Order Exists
                                  </StyledText>
                                  <StyledText
                                    textColor={
                                      theme?.CorporateTheme?.colors
                                        ?.textSecondary
                                    }
                                    fontSize="12px"
                                    fontWeight={400}
                                  >
                                    Blocks users from starting a renewal process
                                    if they have an existing unpaid invoice or
                                    pending order.
                                  </StyledText>
                                </div>
                                <ToggleSwitch
                                  // label="Enable membership certificates?"
                                  checked={values.preventRenewalOpenOrderExists}
                                  activeColor={
                                    theme.CorporateTheme.colors.primary
                                  }
                                  inactiveColor={
                                    theme.CorporateTheme.colors.textSecondary
                                  }
                                  knobColor={
                                    theme.CorporateTheme.colors.background
                                  }
                                  onChange={() =>
                                    setFieldValue(
                                      "preventRenewalOpenOrderExists",
                                      !values.preventRenewalOpenOrderExists,
                                    )
                                  }
                                  // disabled={isView}
                                  noPaddingNeeded
                                  showTitle={false}
                                />
                              </div>

                              <div className="mb-3 d-flex align-items-center justify-content-between">
                                <div>
                                  <StyledText
                                    className="mb-1"
                                    textColor={
                                      theme?.CorporateTheme?.colors?.mirage
                                    }
                                  >
                                    Requires Approval
                                  </StyledText>
                                  <StyledText
                                    textColor={
                                      theme?.CorporateTheme?.colors
                                        ?.textSecondary
                                    }
                                    fontSize="12px"
                                    fontWeight={400}
                                  >
                                    Access granted only after admin review.
                                  </StyledText>
                                </div>
                                <ToggleSwitch
                                  // label="Enable membership certificates?"
                                  checked={values.requireApproval}
                                  activeColor={
                                    theme.CorporateTheme.colors.primary
                                  }
                                  inactiveColor={
                                    theme.CorporateTheme.colors.textSecondary
                                  }
                                  knobColor={
                                    theme.CorporateTheme.colors.background
                                  }
                                  onChange={() =>
                                    setFieldValue(
                                      "requireApproval",
                                      !values.requireApproval,
                                    )
                                  }
                                  // disabled={isView}
                                  noPaddingNeeded
                                  showTitle={false}
                                />
                              </div>

                              <hr
                                style={{
                                  color: theme.CorporateTheme.colors.border,
                                  opacity: 1,
                                }}
                              />

                              <StyledText className="mb-4">
                                Form Settings
                              </StyledText>

                              <div className="mb-3">
                                <StyledText
                                  className=" mb-2"
                                  textColor={
                                    theme.CorporateTheme?.colors?.headerColor
                                  }
                                >
                                  Joining Form
                                </StyledText>
                                <FormikSelect
                                  // className={`${id && "class-disable not-allowed"}`}
                                  options={[
                                    {
                                      label: "Basic membership form",
                                      value: "basic-membership-form",
                                    },
                                    { label: "Form 1", value: "form-1" },
                                    { label: "Form 2", value: "form-2" },
                                  ]}
                                  name="joiningForm"
                                  placeholder="Select joining form"
                                  onChange={(
                                    val:
                                      | string
                                      | {
                                          label: string;
                                          value: number | string;
                                        },
                                  ) => {
                                    const value =
                                      typeof val === "string" ? val : val.value;
                                    setFieldValue("joiningForm", value);
                                    // setSelectedRole(String(value));
                                  }}
                                  // disabled={id ? true : false}
                                />
                              </div>

                              <div className="mb-3">
                                <StyledText
                                  className=" mb-2"
                                  textColor={
                                    theme.CorporateTheme?.colors?.headerColor
                                  }
                                >
                                  Renewal Form
                                </StyledText>
                                <FormikSelect
                                  // className={`${id && "class-disable not-allowed"}`}
                                  options={[
                                    {
                                      label: "Renewal membership form",
                                      value: "renewal-membership-form",
                                    },
                                    { label: "Form 1", value: "form-1" },
                                    { label: "Form 2", value: "form-2" },
                                  ]}
                                  name="renewalForm"
                                  placeholder="Select renewal form"
                                  onChange={(
                                    val:
                                      | string
                                      | {
                                          label: string;
                                          value: number | string;
                                        },
                                  ) => {
                                    const value =
                                      typeof val === "string" ? val : val.value;
                                    setFieldValue("renewalForm", value);
                                    // setSelectedRole(String(value));
                                  }}
                                  // disabled={id ? true : false}
                                />
                              </div>
                            </>
                          )}

                          {/* {!nonExpiringMembership && (
                                    <div className='mb-3 d-flex align-items-center gap-2'>
                                        <FormikCheckBox 
                                            name='allowAutoRenew'
                                            label='Allow auto renew this membership?'
                                            borderColor={theme.CorporateTheme?.colors?.textSecondary}
                                            bgColor={theme.CorporateTheme?.colors?.primary}
                                            checkColor="white"
                                            color={theme.CorporateTheme?.colors?.headerColor}
                                            size={15}
                                        />
                                    </div>
                                )} */}
                          {/* <div className='mb-3 d-flex align-items-center gap-2'>
                                    <FormikCheckBox 
                                        name='allowExpiredToCurrent'
                                        label='Allow expired members to become current upon renewal'
                                        borderColor={theme.CorporateTheme?.colors?.textSecondary}
                                        bgColor={theme.CorporateTheme?.colors?.primary}
                                        checkColor="white"
                                        color={theme.CorporateTheme?.colors?.headerColor}
                                        size={15}
                                    />
                                    <StyledToolTip title={`Expired members can review directly withoutpaying for missed membership years.`} placement='bottom' childrenClass={'ms-0'}>
                                        <TooltipIcon color="#767476" />
                                    </StyledToolTip>
                                </div>

                                <div className='mb-3 d-flex align-items-center gap-2'>
                                    <FormikCheckBox 
                                        name='retainJoinDate'
                                        label='Retain join date for dropped members'
                                        borderColor={theme.CorporateTheme?.colors?.textSecondary}
                                        bgColor={theme.CorporateTheme?.colors?.primary}
                                        checkColor="white"
                                        color={theme.CorporateTheme?.colors?.headerColor}
                                        size={15}
                                    />
                                    <StyledToolTip title={`When a dropped members rejoin later, their original join date will remain the same.`} placement='bottom' childrenClass={'ms-0'}>
                                        <TooltipIcon color="#767476" />
                                    </StyledToolTip>
                                </div> */}

                          {/* <div className='mb-3 d-flex align-items-center gap-2'>
                                    <FormikCheckBox 
                                        name='requireApproval'
                                        label='Requires Approval'
                                        borderColor={theme.CorporateTheme?.colors?.textSecondary}
                                        bgColor={theme.CorporateTheme?.colors?.primary}
                                        checkColor="white"
                                        color={theme.CorporateTheme?.colors?.headerColor}
                                        size={15}
                                    />
                                </div> */}
                        </div>
                      )}

                      {activeTab === "priceArchitecture" && (
                        <div className="" id="price-architecture">
                          <PriceAndArchitecture
                            setFieldValue={setFieldValue}
                            values={values}
                          />
                        </div>
                      )}

                      {activeTab === "glConfig" && (
                        <div className="" id="gl-configuration">
                          GL Configuration content goes here
                        </div>
                      )}

                      {activeTab === "emailConfig" && (
                        <div className="" id="email-confirmation">
                          Email Confirmation content goes here
                        </div>
                      )}
                    </div>
                  </FormSection>
                </div>
                <FormActions>
                    <Button theme={theme} type="button" $variant="cancel" onClick={onClose} /*disabled={isLoading || isUpdating}*/ >
                        <CloseIcon color={theme.CorporateTheme.colors.textSecondary} /> Cancel
                    </Button>
                    {activeTab === 'termsLifecycle' &&
                    <Button theme={theme} type="button" $variant="add" onClick={() => setActiveTab('priceArchitecture')} /*disabled={isLoading || isUpdating}*/>
                        {/* <DocumentIcon color={theme.CorporateTheme.colors.background} /> */}
                        {/* <ReceiptIcon color={theme.CorporateTheme.colors.background} /> */}
                        Next
                        {/* {mode === 'edit' ? (isUpdating ? 'Saving...' : 'Save Changes') : (isLoading ? 'Creating...' : 'Create Form')} */}
                    </Button>
                    }
                    {activeTab === 'priceArchitecture' &&
                      <Button theme={theme} type="submit" $variant="add" /*onClick={handleSubmit} disabled={isLoading || isUpdating}*/>
                          {/* <DocumentIcon color={theme.CorporateTheme.colors.background} /> */}
                          <ReceiptIcon color={theme.CorporateTheme.colors.background} />
                          Create Membership
                          {/* {mode === 'edit' ? (isUpdating ? 'Saving...' : 'Save Changes') : (isLoading ? 'Creating...' : 'Create Form')} */}
                      </Button>
                    }
                </FormActions>
              </Form>
            );
          }}
        </Formik>
      </ModalContent>
    </ModalOverlay>
  );
};

export default AddNewMembershipType;

const ModalContent = styled.div<{ $expanded?: boolean; theme: any }>`
  background: ${({ theme }) => theme.CreateFormTheme.colors.background};
  padding: 0 ${CreateFormTheme.spacing.lg} ${CreateFormTheme.spacing.lg};
  width: 100%;
  overflow-y: auto;
  display: flex;
  flex-direction: column;

  /* default for larger monitors */
  max-width: 60vw;

  /* laptops (adjust breakpoint as needed) */
  @media (max-width: 1640px) {
    max-width: 75vw;
  }
`;

const FormContainer = styled.div<{ theme: any }>`
  border: 1px solid ${({ theme }) => theme?.CorporateTheme?.colors?.border};
  border-radius: 5px 0px 0px 5px;
  padding: 20px 20px 30px 20px;
`;

const FormSection = styled.div<{ theme: any }>`
  background-color: ${({ theme }) => theme?.CreateFormTheme?.colors?.addressBG};
  border-radius: 0px 5px 5px 0px;
`;

const RadioButton = styled.label<{ theme: any }>`
  display: flex;
  gap: 10px;
  input[type="radio"] {
    accent-color: ${({ theme }) => theme.CorporateTheme.colors.primary};
  }
`;

const TabButton = styled.div<{ active: boolean; theme: any }>`
  font-size: 14px;
  font-weight: ${({ active }) => (active ? 600 : 400)};
  padding: 7px 10px;
  background-color: ${({ active, theme }) =>
    active
      ? theme?.CorporateTheme?.colors?.primary
      : theme?.CorporateTheme?.colors?.bgTabNotActive};
  color: ${({ active, theme }) =>
    active
      ? theme?.CorporateTheme?.colors?.background
      : theme?.CorporateTheme?.colors?.headerColor};
  border-radius: 50px;
  width: fit-content;
  display: inline-flex;
  justify-content: center;
  align-items: center;
  text-align: center;

  cursor: pointer;
  transition: all 0.2s ease;
`;
